<template>
  <div class="order-status">
    <template v-if="isNationwideService">
      <Idle
        v-if="accepted"
        icon
        :title="$t(`record.message_v2.default.ACCEPTED_NATIONWIDE.title`)"
      />
      <Idle
        v-else-if="showIdle"
        icon
        :title="$t(`record.message_v2.default.IDLE_NATIONWIDE.title`)"
        :desc="mainStatusMessage.desc_back"
      />
      <Idle
        v-else-if="inProcess"
        icon
        :title="$t(`record.message_v2.default.IN_PROCESS_NATIONWIDE.title`)"
        :desc="mainStatusMessage.desc_back"
      />
      <Idle
        v-else-if="completed"
        icon
        :title="$t(`record.message_v2.default.COMPLETED_NATIONWIDE.title`)"
      />
      <template v-else-if="cancelled">
        <Idle icon :title="$t(`record.message_v2.default.CANCELLED_NATIONWIDE.title`)" />
      </template>
    </template>
    <template v-else>
      <SearchingSupplier
        v-if="showSearchingSupplier"
        :label="$t(`record.message_v2.default.ASSIGNING.title`)"
      />

      <Idle
        v-else-if="showIdle"
        icon
        :title="$t(`record.message_v2.default.IDLE.title`)"
        :desc="mainStatusMessage.desc_back"
      />
      <Idle v-else-if="confirming" icon :title="$t(`record.message_v2.default.CONFIRMING.title`)" />
      <template v-else-if="cancelled">
        <Idle icon :title="$t(`record.message_v2.default.CANCELLED.title`)" />
      </template>
      <Idle
        v-else-if="paying"
        button
        :buttonLabel="$t('payment_method.pay_now')"
        :title="$t(`record.message_v2.default.PAYING.title`)"
        :desc="$t('record.message_v2.default.PAYING.desc')"
        @onClickButton="handleClickPayNow"
      />
      <div v-else>
        <OrderMainStatus
          :order="order"
          :frontLabel="mainStatusMessage.desc_front"
          :backLabel="mainStatusMessage.desc_back"
        />

        <v-list-tile
          class="order-status__vaccinated_status"
          :title="$t('record.supplier_vaccinated_status')"
          v-if="vaccinatedDriver"
        >
          <v-list-tile-avatar>
            <SvgIcon icon="Protection" width="24" height="24" />
          </v-list-tile-avatar>

          <v-list-tile-content>
            <v-list-tile-title>{{ $t('record.supplier_vaccinated_status') }}</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>

        <div class="order-status__rating" v-if="ratingVisible">
          <RatingModal :order="order" ref="ratingModal" @onUpdateRating="updateUserRating" />
        </div>

        <div class="order-status__rating-success_modal">
          <v-dialog
            v-if="!ratingVisible && isShowRateSuccessDialog"
            v-model="isShowRateSuccessDialog"
            max-width="376px"
            content-class="rating-modal__success"
          >
            <!-- <v-icon color="grey" @click="closeDialog">close</v-icon> -->

            <SvgIcon
              class="icon-feedback"
              height="65"
              width="65"
              :icon="dataRateSuccessDialog.icon"
            />
            <p v-dompurify-html="dataRateSuccessDialog.text"></p>

            <v-btn depressed color="accent" @click="closeDialog">{{ $t('close') }}</v-btn>
          </v-dialog>
        </div>

        <TipForDriverModal :order="order" v-if="completed && isTipAllowed" />

        <!-- v-if="!ratingVisible && order.supplier_id" -->
        <SupplierInfo
          :order="order"
          :licensePlate="order.supplier_plate_number"
          :phoneNumber="order.supplier_id"
          :hideMessageChat="hideMessageChat"
          :showRatingNoteExpire="!ratingVisible"
        />

        <div class="order-status__timeline" v-if="isShowTimeline">
          <OrderTimeline :order="order" :porInfo="porInfo" :eta="supplierEta" />
        </div>

        <div class="order-status__expand-btn" @click="handleShowTimeline" data-test-id="arrowUpBtn">
          <span>{{ expandLabel }}</span>
          <SvgIcon
            class="icon"
            height="22"
            width="14"
            :icon="isShowTimeline ? 'ArrowUp' : 'ArrowDown'"
          />
        </div>
      </div>

      <TipForDriverSuccess />
    </template>
  </div>
</template>

<script>
import SearchingSupplier from './order_status/SearchingSupplier.vue'
import Idle from './order_status/Idle.vue'
import SupplierInfo from './order_status/SupplierInfo.vue'
import OrderMainStatus from './order_status/OrderMainStatus.vue'
import OrderTimeline from './order_status/OrderTimeline.vue'
import RatingModal from '@/components/record_detail/RatingModal.vue'
import TipForDriverModal from '@/components/record_detail_v2/tip_for_driver/TipForDriverModal.vue'
import TipForDriverSuccess from '@/components/record_detail_v2/tip_for_driver/TipForDriverSuccess.vue'

import { mapState, mapGetters } from 'vuex'

const ARRIVED_ETA_LIMIT = 1
const RATING_VISIBLE_DURATION = 3 * 24 * 60 * 60

export default {
  name: 'OrderStatus',
  components: {
    SearchingSupplier,
    Idle,
    SupplierInfo,
    OrderMainStatus,
    OrderTimeline,
    RatingModal,
    TipForDriverModal,
    TipForDriverSuccess
  },
  props: {
    order: {
      type: Object
    },
    hideMessageChat: {
      type: Boolean,
      default: false
    },
    deliveryType: {
      type: String
    },
    onSubmitSupplierRate: {
      type: Function
    },
    porInfo: {
      type: String,
      default: ''
    },
    isTipAllowed: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isShowTimeline: false,
      isShowRating: false,
      mainStatusMessage: {},
      isShowRateSuccessDialog: false,
      dataRateSuccessDialog: {
        icon: '',
        text: ''
      }
    }
  },
  computed: {
    ...mapState({
      supplierEta: (state) => state.supplierLocation.supplierEta
    }),
    ...mapGetters([
      'isExistedZaloPayWallet',
      'isZaloPayWalletUnavailable',
      'isExistedShopeePayWallet',
      'isShopeePayWalletUnavailable'
    ]),
    isNationwideService() {
      return (
        this.order &&
        this.order.service &&
        this.order.service.delivery_type === this.$constants.DELIVERY_TYPE.NATIONWIDE
      )
    },
    showRideConnection() {
      return (
        this.order.status === this.$constants.ORDER_STATUS.ACCEPTED &&
        this.supplierLocation &&
        this.supplierLocation.sub_status === this.$constants.SUPPLIER_SUB_STATUS.COMPLETING
      )
    },
    showSearchingSupplier() {
      return this.order.status === this.$constants.ORDER_STATUS.ASSIGNING
    },
    accepted() {
      return this.order.status === this.$constants.ORDER_STATUS.ACCEPTED
    },
    inProcess() {
      return this.order.status === this.$constants.ORDER_STATUS.IN_PROCESS
    },
    showIdle() {
      return this.order.status === this.$constants.ORDER_STATUS.IDLE
    },
    cancelled() {
      return this.order.status === this.$constants.ORDER_STATUS.CANCELLED
    },
    confirming() {
      return this.order.status === this.$constants.ORDER_STATUS.CONFIRMING
    },
    completed() {
      return this.order.status === this.$constants.ORDER_STATUS.COMPLETED
    },
    paying() {
      return this.order.status === this.$constants.ORDER_STATUS.PAYING
    },
    ratingVisible() {
      const compeleteTime = this.$moment.unix(this.order.complete_time)
      const diff = this.$moment().diff(compeleteTime, 'seconds')

      return (
        this.order.supplier_id &&
        (!this.order.rating_by_user || this.order.rating_by_user === 0) &&
        this.order.status === this.$constants.ORDER_STATUS.COMPLETED &&
        diff < RATING_VISIBLE_DURATION
      )
    },
    expandLabel() {
      return this.isShowTimeline ? this.$t('collapse') : this.$t('record.order_status_expand')
    },
    vaccinatedDriver() {
      return this.order.covid_19 && this.$constants.VACCINATED_STATUS.includes(this.order.covid_19)
    }
  },
  methods: {
    closeDialog() {
      this.isShowRateSuccessDialog = false
    },
    handleClickPayNow() {
      if (!this.paying) {
        return
      }

      if (
        this.order.payment_method === this.$constants.PAYMENT_METHODS.ZALOPAY &&
        this.isExistedZaloPayWallet
      ) {
        if (!this.isZaloPayWalletUnavailable) {
          this.$store.dispatch('setAutoDebitZaloPay', true)
        }
      }

      if (
        this.order.payment_method === this.$constants.PAYMENT_METHODS.SHOPEEPAY &&
        this.isExistedShopeePayWallet
      ) {
        if (!this.isShopeePayWalletUnavailable) {
          this.$store.dispatch('setAutoDebitShopeePay', true)
        }
      }

      if (this.order.payment_method === this.$constants.PAYMENT_METHODS.CARD) {
        this.$store.dispatch('setMethodPicked', this.order.payment_details)
      }

      this.$router.replace({
        name: 'payment',
        params: {
          id: this.order._id,
          isPayOrderWithAutoDebit: true,
          inAppRedirect: true
        },
        query: this.$route.query
      })
    },
    handleShowTimeline() {
      this.isShowTimeline = !this.isShowTimeline
    },
    updateUserRating(newValue) {
      this.$emit('onSubmitSupplierRate', newValue)

      this.isShowRateSuccessDialog = true

      this.dataRateSuccessDialog.icon = newValue.rating === 5 ? `RatingFunny` : `RatingSad`

      newValue.rating === 5
        ? (this.dataRateSuccessDialog.text = this.$t('rating.message.success', {
            url: '/assets/images/icons/StarSelected.svg'
          }))
        : (this.dataRateSuccessDialog.text = this.$t('rating.message.failed'))
    },
    // checkOrderStatusToShowRatingModal() {
    //   if (
    //     this.order.status === this.$constants.ORDER_STATUS.COMPLETED &&
    //     (!this.order.rating_by_user || this.order.rating_by_user === 0)
    //   ) {
    //     this.$nextTick(() => {
    //       this.$refs.ratingModal.showDialog = true;
    //     })
    //   }
    // },
    formatTime(unixTime) {
      if (unixTime) {
        if (isNaN(unixTime)) return unixTime
        else {
          let format = 'DD/MM, h:mm A'
          const mTime = this.$moment.unix(unixTime)
          if (this.$moment().isSame(mTime, 'day')) {
            format = 'h:mm A'
          }

          return mTime.format(format)
        }
      }

      return ''
    },
    getStatusMessage() {
      let message = {}
      if (this.order.status === this.$constants.ORDER_STATUS.COMPLETED) {
        const hasFailedDelivery = this.order.path.findIndex((point) => {
          return point.status === this.$constants.ORDER_STATUS.FAILED
        })
        const returnTime = this.order.path[0].return_time || this.order.return_time
        let msgStatus = hasFailedDelivery > -1 && !returnTime ? 'FAILED' : 'COMPLETED'
        message = {
          title: this.$t(`record.message_v2.default.${msgStatus}.title`),
          desc_front: this.$t(`record.message_v2.default.${msgStatus}.desc_front`),
          desc_back: this.$t(`record.message_v2.default.${msgStatus}.desc_back`)
        }

        if (msgStatus === 'COMPLETED' && !this.order.rating_by_user) {
          message.desc_back += ` ${this.$t('record.user_rate_asking_completed')}`
        }
      }
      // Special status for SAME DAY
      else if (this.deliveryType === this.$constants.DELIVERY_TYPE.SAMEDAY) {
        const orderStatus = this.order.status
        const formatTime = 'HH:mm'
        let fromTime, toTime, date, msgStatus, desc_back

        switch (orderStatus) {
          case this.$constants.ORDER_STATUS.IDLE: {
            // toTime = order_time + 120 mins
            const orderTime = this.$moment.unix(this.order.order_time)

            fromTime = orderTime.format(formatTime)
            toTime = this.$moment.unix(this.order.order_time + 120 * 60).format(formatTime)
            date = orderTime.format('DD/MM')

            desc_back = this.$t(`record.message_v2.4h.${orderStatus}.desc_back_with_date`, {
              from_time: fromTime,
              to_time: toTime,
              date
            })

            if (this.$moment().isSame(orderTime, 'day')) {
              desc_back = this.$t(`record.message_v2.4h.${orderStatus}.desc_back`, {
                from_time: fromTime,
                to_time: toTime
              })
            }

            break
          }
          case this.$constants.ORDER_STATUS.ACCEPTED: {
            const supplierLocation = this.$store.getters.supplierLocation
            if (supplierLocation) {
              if (this.supplierEta !== undefined && this.supplierEta < ARRIVED_ETA_LIMIT) {
                msgStatus = 'ARRIVED'
              } else {
                msgStatus = 'PICKING_UP'
                desc_back = this.$t(`record.message_v2.default.${msgStatus}.desc_back_eta`, {
                  time: this.supplierEta
                })
              }
            } else {
              msgStatus = 'ACCEPTED'

              fromTime = this.$moment.unix(this.order.accept_time).format(formatTime)
              toTime = this.$moment.unix(this.order.accept_time + 60 * 60).format(formatTime)
            }
            break
          }
          case this.$constants.ORDER_STATUS.IN_PROCESS: {
            if (this.order.path[1].status) {
              msgStatus =
                this.order.path[1].status === this.$constants.ORDER_STATUS.FAILED
                  ? 'FAILED'
                  : 'DELIVERY'
            } else {
              const supplierLocation = this.$store.getters.supplierLocation
              if (
                supplierLocation &&
                this.supplierEta !== undefined &&
                this.supplierEta < ARRIVED_ETA_LIMIT
              ) {
                msgStatus = 'DROPPING'
              } else {
                msgStatus = 'IN_PROCESS'
                fromTime = this.order.pickup_time
                toTime = this.order.pickup_time + 120 * 60
              }
            }
            break
          }
        }

        msgStatus = msgStatus || orderStatus
        fromTime = this.formatTime(fromTime)
        toTime = this.formatTime(toTime)

        message = {
          title: this.$t(`record.message_v2.4h.${msgStatus}.title`),
          desc_front: this.$t(`record.message_v2.4h.${msgStatus}.desc_front`),
          desc_back:
            desc_back ||
            this.$t(`record.message_v2.4h.${msgStatus}.desc_back`, {
              from_time: fromTime,
              to_time: toTime,
              date: date
            })
        }

        if (msgStatus === 'DELIVERY' && !this.order.rating_by_user) {
          message.desc_back += ` ${this.$t('record.user_rate_asking_delivery')}`
        }
      } else {
        let fromTime, toTime
        if (this.order.status === this.$constants.ORDER_STATUS.IDLE) {
          fromTime = this.order.order_time
          toTime = this.order.order_time + 120 * 60
        }

        let msgStatus = this.order.status.split(' ').join('_')
        fromTime = this.formatTime(fromTime)
        toTime = this.formatTime(toTime)

        // Allow estimated time of arrival
        const allowEta = this.$constants.SERVICES_ALLOW_ETA.some((s) => {
          return this.order.service_id.includes(s)
        })

        let desc_back = ''

        if (allowEta && this.order.status === this.$constants.ORDER_STATUS.ACCEPTED) {
          const supplierLocation = this.$store.getters.supplierLocation
          if (supplierLocation) {
            if (this.supplierEta !== undefined && this.supplierEta < ARRIVED_ETA_LIMIT) {
              msgStatus = 'ARRIVED'
            } else {
              msgStatus = 'PICKING_UP'
              desc_back = this.$t(`record.message_v2.default.${msgStatus}.desc_back_eta`, {
                time: this.supplierEta
              })
            }
          }
        }

        if (
          allowEta &&
          this.supplierEta !== undefined &&
          this.order.status === this.$constants.ORDER_STATUS.IN_PROCESS &&
          this.order.path &&
          Array.isArray(this.order.path) &&
          this.order.path.length === 2
        ) {
          if (this.supplierEta < ARRIVED_ETA_LIMIT) {
            msgStatus = 'DROPPING'
            desc_back = this.$t('record.message_v2.default.DROPPING.desc_back')
          } else {
            desc_back = this.$t(`record.message_v2.default.${msgStatus}.desc_back_eta`, {
              time: this.supplierEta
            })
          }
        }

        if (!desc_back) {
          desc_back = this.$t(`record.message_v2.default.${msgStatus}.desc_back`, {
            from_time: fromTime,
            to_time: toTime
          })
        }

        message = {
          title: this.$t(`record.message_v2.default.${msgStatus}.title`),
          desc_front: this.$t(`record.message_v2.default.${msgStatus}.desc_front`),
          desc_back
        }
      }
      return message
    }
  },
  mounted() {
    if (this.order) {
      this.mainStatusMessage = this.getStatusMessage()
    }
  },
  watch: {
    order(val, oldVal) {
      if (val.status !== oldVal.status) {
        // this.checkOrderStatusToShowRatingModal()
        this.mainStatusMessage = this.getStatusMessage()
      }

      if (val.rating_by_user !== oldVal.rating_by_user) {
        this.mainStatusMessage = this.getStatusMessage()
      }
    },
    supplierEta(val, oldVal) {
      if (
        val !== oldVal &&
        (this.order.status === this.$constants.ORDER_STATUS.ACCEPTED ||
          this.order.status === this.$constants.ORDER_STATUS.IN_PROCESS)
      ) {
        this.mainStatusMessage = this.getStatusMessage()
      }
    }
  }
}
</script>
<i18n src="@/locales2/share/ratingValue.json"></i18n>

<style lang="scss" scoped>
.order-status {
  .order-status__timeline {
    border-bottom: 1px solid #f2f2f2;
    padding: 12px 16px 12px 18px;
  }

  .order-status__expand-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 38px;
    border-top: 1px solid #f2f2f2;
    cursor: pointer;
    color: #7a8189;
    font-weight: 500;

    &:hover {
      background-color: #f8f8f8;
      border-bottom-left-radius: 8px;
      border-bottom-right-radius: 8px;
    }
  }

  .order-status__rating {
    padding-bottom: 16px;
    display: flex;
    justify-content: center;
  }
}
</style>

<style lang="scss">
.rating-modal__success {
  .v-btn {
    text-transform: none !important;
  }
}

.order-status {
  .order-status__expand-btn {
    .icon {
      margin-left: 6px;

      svg {
        path {
          fill: #7a8189;
        }
      }
    }
  }

  &__vaccinated_status {
    margin: 4px 16px 12px;
    background: #f0f5ff;
    border-radius: 2px;
    border-bottom: none !important;

    + .supplier-info {
      border-top: none;
    }

    + .order-status__rating {
      padding-top: 4px;
    }

    .v-list__tile {
      padding: 0;
      height: 40px;

      &__action,
      &__avatar {
        min-width: 48px;
        justify-content: flex-end;
      }

      &__title {
        height: auto;
        font-size: 14px;
        line-height: 22px;
        color: #21272a;
      }

      &__content {
        padding-right: 16px;
      }
    }
  }
}

@media screen and (max-width: 480px) {
  .order-status__vaccinated_status {
    margin: 0;
    background: #fff;
    border-radius: 0;
    border-top: 1px solid #f2f2f2;

    + .order-status__rating {
      padding-top: 16px;
      border-top: 1px solid #f2f2f2;
    }

    .v-list__tile {
      justify-content: center;

      &__title {
        font-size: 12px;
        line-height: 16px;
      }
    }
  }
}
</style>
